import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import AppLayout from "@/components/AppLayout";
import Login from "@/pages/Login";
import ForgotPassword from "@/pages/ForgotPassword";
import Dashboard from "@/pages/Dashboard";
import Meetings from "@/pages/Meetings";
import MeetingCreate from "@/pages/MeetingCreate";
import MeetingEdit from "@/pages/MeetingEdit";
import MeetingDetail from "@/pages/MeetingDetail";
import LiveMeeting from "@/pages/LiveMeeting";
import VoiceEnrollment from "@/pages/VoiceEnrollment";
import Participants from "@/pages/Participants";
import SearchPage from "@/pages/SearchPage";
import Reports from "@/pages/Reports";
import CalendarPage from "@/pages/CalendarPage";
import Settings from "@/pages/Settings";
import Users from "@/pages/Users";
import Summaries from "@/pages/Summaries";
import KnowledgeCenter from "@/pages/KnowledgeCenter";
import { Profile, ChangePassword } from "@/pages/Profile";
import { NotFound, ServerError } from "@/pages/ErrorPages";
import { Logo } from "@/components/Logo";

const Splash = () => (
  <div className="min-h-screen grid place-items-center bg-[#F8FAFC]">
    <div className="animate-pulse"><Logo size={40} tagline /></div>
  </div>
);

const Protected = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <Splash />;
  if (!user) return <Navigate to="/login" replace />;
  return children;
};

const PublicOnly = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <Splash />;
  if (user) return <Navigate to="/dashboard" replace />;
  return children;
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Toaster position="top-right" richColors />
        <Routes>
          <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
          <Route path="/forgot-password" element={<PublicOnly><ForgotPassword /></PublicOnly>} />
          <Route path="/500" element={<ServerError />} />
          <Route element={<Protected><AppLayout /></Protected>}>
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/meetings" element={<Meetings />} />
            <Route path="/meetings/new" element={<MeetingCreate />} />
            <Route path="/meetings/:id" element={<MeetingDetail />} />
            <Route path="/meetings/:id/edit" element={<MeetingEdit />} />
            <Route path="/live" element={<LiveMeeting />} />
            <Route path="/live/:id" element={<LiveMeeting />} />
            <Route path="/summaries" element={<Summaries />} />
            <Route path="/participants" element={<Participants />} />
            <Route path="/voice-enrollment" element={<VoiceEnrollment />} />
            <Route path="/search" element={<SearchPage />} />
            <Route path="/reports" element={<Reports />} />
            <Route path="/calendar" element={<CalendarPage />} />
            <Route path="/settings" element={<Settings />} />
            <Route path="/users" element={<Users />} />
            <Route path="/profile" element={<Profile />} />
            <Route path="/change-password" element={<ChangePassword />} />
            <Route path="/knowledge-center" element={<KnowledgeCenter />} />
          </Route>
          <Route path="*" element={<NotFound />} />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
